import { TemplateType } from "../../common/EmailDetails";
import {
  JobOrder,
  JobOrderDuplicate,
  JobOrderExecView,
  JobOrderMetadata,
  VesselJobOrderAggregate,
} from "../../common/JobOrder";
import { jobOrderSearchParams } from "../../common/JobOrder";
import { JobOrderChange } from "../../common/JobOrderHistory";
import { PaginatedResponse } from "../../common/PaginatedResponse";
import { baseApi } from "./base";

export const jobOrderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobOrders: builder.query<
      PaginatedResponse<JobOrder>,
      Partial<jobOrderSearchParams>
    >({
      query: ({
        assigneeId,
        vesselId,
        port,
        rating,
        compatibleWithRating,
        jobOrderID,
        fillByAfterDate,
        fillByBeforeDate,
        completed,
        notApplicable,
        completedOrNotApplicable,
        deleted,
        archived,
        critical,
        newJobOrder,
        modifiedByCaptain,
        publishStatuses,
        smis,
        profileId,
        page,
        size,
        sortColumn,
        sortDirection,
      }) => {
        let url = `jobOrders/${
          !!assigneeId ? "&assigneeId=" + assigneeId : ""
        }${!!vesselId ? "&vesselId=" + vesselId : ""}${
          !!port ? "&port=" + port : ""
        }${!!rating ? "&rating=" + rating : ""}${
          !!compatibleWithRating
            ? "&compatibleWithRating=" + compatibleWithRating
            : ""
        }${!!jobOrderID ? "&jobOrderID=" + jobOrderID : ""}${
          !!fillByAfterDate ? "&fillByAfterDate=" + fillByAfterDate : ""
        }${!!fillByBeforeDate ? "&fillByBeforeDate=" + fillByBeforeDate : ""}${
          !!completed || completed === false ? "&completed=" + completed : ""
        }${
          !!notApplicable || notApplicable === false
            ? "&notApplicable=" + notApplicable
            : ""
        }${
          !!completedOrNotApplicable || completedOrNotApplicable === false
            ? "&completedOrNotApplicable=" + completedOrNotApplicable
            : ""
        }${!!deleted || deleted === false ? "&deleted=" + deleted : ""}${
          !!archived || archived === false ? "&archived=" + archived : ""
        }${!!critical || critical === false ? "&critical=" + critical : ""}${
          !!newJobOrder || newJobOrder === false
            ? "&newJobOrder=" + newJobOrder
            : ""
        }${
          !!modifiedByCaptain || modifiedByCaptain === false
            ? "&modifiedByCaptain=" + modifiedByCaptain
            : ""
        }${
          !!publishStatuses && publishStatuses?.length > 0
            ? "&publishStatuses=" + publishStatuses
            : ""
        }${!!smis ? "&smis=" + smis.replaceAll("#", "%23") : ""}${
          !!profileId ? "&profileId=" + profileId : ""
        }${"&page=" + page}${"&size=" + size}${
          !!sortColumn && sortColumn !== "" ? "&sortColumn=" + sortColumn : ""
        }${
          !!sortColumn && sortColumn !== ""
            ? "&sortDirection=" + sortDirection?.toUpperCase()
            : ""
        }
        `;
        url = url.replace("jobOrders/&", "jobOrders?");
        return {
          method: "GET",
          url,
        };
      },
      providesTags: (result) =>
        result && !!result.content
          ? [
              ...result.content.map(({ id }) => ({
                type: "JobOrder" as const,
                id,
              })),
              { type: "JobOrder", id: "LIST" },
            ]
          : [{ type: "JobOrder", id: "LIST" }],
    }),
    getProfileJobOrders: builder.query<JobOrder[], {}>({
      query: () => {
        return {
          method: "GET",
          url: `jobOrders/profile`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "ProfileJobOrder" as const,
                id,
              })),
              { type: "ProfileJobOrder", id: "LIST" },
            ]
          : [{ type: "ProfileJobOrder", id: "LIST" }],
    }),
    getJobPostings: builder.query<JobOrder[], {}>({
      query: () => {
        return {
          method: "GET",
          url: `jobOrders/postings`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "JobPosting" as const, id })),
              { type: "JobPosting", id: "LIST" },
            ]
          : [{ type: "JobPosting", id: "LIST" }],
    }),
    getJobOrder: builder.query<JobOrder, String>({
      query: (jobOrderId) => {
        return {
          method: "GET",
          url: `jobOrders/${jobOrderId}`,
        };
      },
    }),
    createJobOrder: builder.mutation<JobOrder, Partial<JobOrder>>({
      query: (jobOrder) => {
        return {
          method: "POST",
          url: "jobOrders/",
          body: jobOrder,
        };
      },
      invalidatesTags: [{ type: "JobOrder", id: "LIST" }],
    }),
    markJobOrderDeleted: builder.mutation<
      JobOrder,
      { jobOrderId: string; note: string }
    >({
      query: ({ jobOrderId, note }) => {
        return {
          method: "POST",
          url: `jobOrders/${jobOrderId}/delete`,
          body: { note },
        };
      },
    }),
    markDuplicatesDeleted: builder.mutation<
      JobOrder,
      { jobOrderIds: number[] }
    >({
      query: ({ jobOrderIds }) => {
        return {
          method: "POST",
          url: `jobOrders/delete?ids=${jobOrderIds.join(",")}`,
          body: { note: "deleted via duplicate detection" },
        };
      },
    }),
    updateJobOrder: builder.mutation<
      JobOrder,
      { jobOrderId: string; jobOrder: Partial<JobOrder>; isPatch?: boolean }
    >({
      query: ({ jobOrderId, jobOrder, isPatch }) => {
        return {
          method: isPatch ? "PATCH" : "PUT",
          url: `jobOrders/${jobOrderId}`,
          body: jobOrder,
        };
      },
      invalidatesTags: (result, error, { jobOrderId }) => [
        { type: "JobOrder", id: jobOrderId },
      ],
    }),
    getVesselJobOrderAggregations: builder.query<
      VesselJobOrderAggregate[],
      { vesselIds?: number[] }
    >({
      query: ({ vesselIds }) => {
        return {
          method: "GET",
          url: `jobOrders/aggregate${
            vesselIds ? `?vesselIds=${vesselIds.join(",")}` : ""
          }`,
        };
      },
    }),
    getJobOrderMetadata: builder.query<JobOrderMetadata, {}>({
      query: () => {
        return {
          method: "GET",
          url: "jobOrders/metadata",
        };
      },
    }),
    getJobOrderExecView: builder.query<
      JobOrderExecView,
      { startDate: string | undefined; endDate: string | undefined }
    >({
      query: ({ startDate, endDate }) => {
        return {
          method: "GET",
          url: `jobOrders/executive${
            !!startDate ? `&fillByAfterDate=${startDate}` : ""
          }${!!endDate ? `&fillByBeforeDate=${endDate}` : ""}`.replace(
            "executive&",
            "executive?"
          ),
        };
      },
    }),
    getDuplicateJobOrders: builder.query<JobOrderDuplicate[], {}>({
      query: () => {
        return {
          method: "GET",
          url: "jobOrders/duplicates",
        };
      },
    }),
    getEmailTemplateNames: builder.query<TemplateType[], number>({
      query: (vesselId) => {
        return {
          method: "GET",
          url: `jobOrders/emailTemplates?vesselId=${vesselId}`,
        };
      },
    }),
    getJobOrderHistory: builder.query<JobOrderChange[], string>({
      query: (jobOrderId) => {
        return {
          method: "GET",
          url: `jobOrders/history?jobOrderId=${jobOrderId}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "JobOrderHistory" as const,
                id,
              })),
              { type: "JobOrderHistory", id: "LIST" },
            ]
          : [{ type: "JobOrderHistory", id: "LIST" }],
    }),
    saveJobOrders: builder.mutation<
      { successCount: number; failedCount: number },
      { jobOrders: Partial<JobOrder>[]; isPatch?: boolean }
    >({
      async queryFn(
        { jobOrders, isPatch },
        _queryApi,
        _extraOptions,
        baseQuery
      ) {
        let successCount: number = 0;
        let failedCount: number = 0;
        let additionalError: string | undefined = undefined;
        for (const jobOrder of jobOrders) {
          if (!jobOrder.id) {
            const response = await baseQuery({
              url: `jobOrders/`,
              method: "POST",
              body: jobOrder,
            });
            if ("error" in response) {
              failedCount += 1;
            } else {
              successCount += 1;
            }
          } else {
            const response = await baseQuery({
              url: `jobOrders/${jobOrder.id}/`,
              method: isPatch ? "PATCH" : "PUT",
              body: jobOrder,
            });
            if ("error" in response) {
              failedCount += 1;
              // exception case for when there is a concurrent editing error
              if (
                (response.error?.data as any[])?.[0]?.errorCode ===
                "joborder.update.edited.failed"
              ) {
                if (!additionalError) {
                  additionalError = `Job Order has already been updated. Please reload the page and re-apply your changes. Affected Job Orders: ${jobOrder.id}`;
                } else {
                  additionalError = additionalError + `, ${jobOrder.id}`;
                }
              }
            } else {
              successCount += 1;
            }
          }
        }
        return { data: { successCount, failedCount, additionalError } };
      },
      invalidatesTags: ["JobOrder", { type: "Dispatch", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetJobOrdersQuery,
  useGetProfileJobOrdersQuery,
  useGetJobPostingsQuery,
  useGetJobOrderQuery,
  useLazyGetJobOrderHistoryQuery,
  useCreateJobOrderMutation,
  useMarkJobOrderDeletedMutation,
  useMarkDuplicatesDeletedMutation,
  useUpdateJobOrderMutation,
  useGetVesselJobOrderAggregationsQuery,
  useGetJobOrderMetadataQuery,
  useGetJobOrderExecViewQuery,
  useGetDuplicateJobOrdersQuery,
  useGetEmailTemplateNamesQuery,
  useSaveJobOrdersMutation,
} = jobOrderApi;
